import DynamicSelect from '@ui-library/dynamicSelect';
import { apiAuthCaller } from '@services/apiCaller';
import useStores from '@stores/useStores';
import { SmartRetry } from '@utils/retry';
import axios from 'axios';
import React, { useState } from 'react';
import { Header } from '@components/sources/source/warehouseSource/configureData/style';
import { reloadButton } from '@components/sources/visualizer/mappingScreens/config/mappingConfig/SelectUniqueIdentifier';
import { getApiErrorMessage } from '@components/common/util/util';
import { FixMe } from '@utils/types';
import { useConnectionState, IField } from '../../visualDataMappingProvider';
import classes from '../mappingConfig/mapping-commons.module.scss';
import { fetchErrorAssets } from '../../errorsView';

interface DestinationConnectionConfigProps {
  onChangeLoadingState?: (loading: boolean) => void;
}
const retry = new SmartRetry<FixMe>({ times: 1 });

const DestinationConnectionConfig = ({
  onChangeLoadingState,
}: DestinationConnectionConfigProps) => {
  const {
    setDestinationConfig,
    destinationConfig,
    setFields,
    ifUpdate,
    viewOnly,
    destination,
    updateSchemaErrors,
  } = useConnectionState();
  const [refresh, setRefresh] = React.useState(false);
  const startReload = () => {
    updateSchemaErrors([fetchErrorAssets('objects')]);
    setRefresh(true);
  };
  const refreshObjectsButton = () => (
    <div className={classes.dropdown_reload_button_wrapper}>
      {reloadButton({ type: 'text', refreshing: refresh, onClick: startReload }, 'objects')}
    </div>
  );

  const timeout = 35000;
  const { userStore, messagesStore, workspaceStore } = useStores();
  const [errMsg, setErrMsg] = useState('');
  const role = destination?.destinationDefinition?.name;
  const showErrorMessage = () =>
    destinationConfig.object && errMsg ? <div className={classes.error_state}>{errMsg}</div> : null;
  return (
    <>
      <div className={classes.head_wrapper}>
        <Header>Where do you want to sync data to?</Header>
      </div>
      <DynamicSelect
        disabled={viewOnly || !!ifUpdate}
        showSearch
        refresh={refresh}
        setRefresh={setRefresh}
        field={{
          method: 'post',
          timeout,
          label: 'Object',
          required: true,
          showSearch: true,
          url: `integrationsInfo/${role}/objects`,
          defaultValue: JSON.stringify(destinationConfig?.object),
          value: JSON.stringify(destinationConfig?.object),
          params: { config: { ...destination.config, destinationId: destination?.id } },
          optionAsValue: true,
          responseKey: 'objects',
          labelKey: 'label',
          valueKey: 'id',
        }}
        onError={(err) => {
          const errorMsg = getApiErrorMessage(err, 'Failed to fetch objects');
          messagesStore.showErrorMessage(errorMsg);
          updateSchemaErrors([fetchErrorAssets('objects', errorMsg)]);
        }}
        onChange={(_, object: FixMe) => {
          if (viewOnly || ifUpdate) {
            return;
          }
          updateSchemaErrors([fetchErrorAssets('objects')]);
          if (object) {
            setDestinationConfig({ object });
            setErrMsg('');
            if (onChangeLoadingState) {
              onChangeLoadingState(true);
            }
            retry
              .exec(() =>
                apiAuthCaller(userStore.token, false, 35000).post(
                  `integrationsInfo/${role}/fields`,
                  {
                    config: {
                      destinationId: destination?.id,
                      workspaceId: workspaceStore.id,
                      ...destination.config,
                    },
                    object: typeof object === 'string' ? { name: object, label: object } : object,
                  },
                  {
                    cancelToken: new axios.CancelToken((c) => {
                      retry.setCancelMethod(c);
                    }),
                  },
                ),
              )
              .then((resp) => {
                updateSchemaErrors([fetchErrorAssets('fields')]);
                const fields = resp?.data?.fields;
                setFields(fields, true);
                if (fields.length === 0) {
                  setErrMsg('No fields found in the selected object');
                } else if (!fields.some((field: IField) => field.isUnique)) {
                  setErrMsg(
                    'The selected object does not have a field that can be used as an identifier',
                  );
                } else {
                  setErrMsg('');
                }
              })
              .catch((err) => {
                if (err.constructor.name === 'Cancel') {
                  return;
                }
                setFields([], true);
                const errorMsg = getApiErrorMessage(err, 'Failed to fetch fields');
                messagesStore.showErrorMessage(errorMsg);
                updateSchemaErrors([fetchErrorAssets('fields', errorMsg)]);
              })
              .finally(() => {
                if (onChangeLoadingState) {
                  onChangeLoadingState(false);
                }
              });
          } else {
            setFields([]);
          }
        }}
      />
      {!viewOnly && !ifUpdate && (
        <>
          {showErrorMessage()}
          {refreshObjectsButton()}
        </>
      )}
    </>
  );
};

export default DestinationConnectionConfig;
